import { PixelIcon } from '@/components/ui';
import { useSound } from '@/contexts';
import { motion } from 'motion/react';
import React from 'react';

export interface GameButtonProps {
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';
  size?: 'small' | 'medium' | 'large';
  icon?: string;
  text: string;
  shortcut?: string;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
  layoutId?: string;
}

const GameButton: React.FC<GameButtonProps> = ({
  onClick,
  variant = 'primary',
  size = 'medium',
  icon,
  text,
  shortcut,
  disabled = false,
  className = '',
  children,
  layoutId,
}) => {
  const { playButtonClick } = useSound();

  const handleClick = () => {
    if (!disabled) {
      playButtonClick();
      onClick();
    }
  };

  const sizeClasses = {
    small: 'btn-small',
    medium: '',
    large: 'btn-large',
  };

  const buttonClass = `btn-${variant} ${sizeClasses[size]} ${
    disabled ? 'disabled' : ''
  } ${className}`.trim();

  return (
    <motion.button
      className={buttonClass} 
      onClick={handleClick} 
      disabled={disabled} 
      type="button"
      layoutId={layoutId}
      whileHover={disabled ? {} : { scale: 1.02 }}
      whileTap={disabled ? {} : { scale: 0.98 }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 25,
      }}
    >
      {icon && (
        <motion.span 
          className="btn-icon"
          layoutId={layoutId ? `${layoutId}-icon` : undefined}
        >
          <PixelIcon name={icon} />
        </motion.span>
      )}
      <motion.span 
        className="btn-text"
        layoutId={layoutId ? `${layoutId}-text` : undefined}
      >
        {text}
      </motion.span>
      {shortcut && (
        <motion.span 
          className="btn-shortcut"
          layoutId={layoutId ? `${layoutId}-shortcut` : undefined}
        >
          {shortcut}
        </motion.span>
      )}
      {children}
    </motion.button>
  );
};

export default GameButton;
